const API_KEY = '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';
const BASE_URL = 'https://openrouter.ai/api/v1';

interface GenerateCodeRequest {
  prompt: string;
  language: string;
  context?: string;
}

interface GenerateCodeResponse {
  code: string;
  explanation?: string;
}

export class APIService {
  private static async makeRequest(endpoint: string, data: any): Promise<any> {
    try {
      const response = await fetch(`${BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${API_KEY}`,
          'Content-Type': 'application/json',
          'HTTP-Referer': window.location.origin,
          'X-Title': 'Codenest AI '
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('API request error:', error);
      throw error;
    }
  }

  static async generateCode(request: GenerateCodeRequest): Promise<GenerateCodeResponse> {
    const systemPrompt = `You are an elite developer. 
    1. Produce the SHORTEST POSSIBLE ${request.language} code for the user request.
    2. Provide a single, very brief sentence explaining what the code does.
    
    Format your response EXACTLY as follows:
    CODE: [your code here]
    EXPLANATION: [your brief explanation here]`;

    const messages = [
      {
        role: 'system',
        content: systemPrompt
      },
      {
        role: 'user',
        content: request.prompt
      }
    ];

    try {
      const response = await this.makeRequest('/chat/completions', {
        model: 'anthropic/claude-3-haiku',
        messages: messages,
        temperature: 0,
        max_tokens: 500,
        stream: false
      });

      const content = response.choices?.[0]?.message?.content || '';

      const codeMatch = content.match(/CODE:\s*([\s\S]*?)(?=\nEXPLANATION:|$)/i);
      const explanationMatch = content.match(/EXPLANATION:\s*([\s\S]*?)$/i);

      let code = codeMatch ? codeMatch[1].trim() : content;
      let explanation = explanationMatch ? explanationMatch[1].trim() : '';

      // Secondary cleanup to ensure no markdown
      code = code.replace(/```[\w]*\n/g, '').replace(/```/g, '').trim();

      return {
        code: code,
        explanation: explanation
      };
    } catch (error) {
      console.error('Code generation failed:', error);
      throw new Error('Failed to generate code. Please try again.');
    }
  }

  static async simplifyError(error: string, language: string): Promise<string> {
    const messages = [
      {
        role: 'system',
        content: `You are a helpful programming tutor. Simplify the following ${language} error message into a single, very short, human-friendly sentence. Explain what went wrong in plain English. Avoid technical jargon where possible.`
      },
      {
        role: 'user',
        content: `Error: ${error}`
      }
    ];

    try {
      const response = await this.makeRequest('/chat/completions', {
        model: 'anthropic/claude-3-haiku',
        messages: messages,
        temperature: 0,
        max_tokens: 150,
        stream: false
      });

      return response.choices?.[0]?.message?.content || 'Something went wrong with your code.';
    } catch (err) {
      console.error('Error simplification failed:', err);
      return 'The code failed to run. Check for syntax errors.';
    }
  }

  static async explainCode(code: string, language: string): Promise<string> {
    const messages = [
      {
        role: 'system',
        content: `You are a code expert. Explain the provided ${language} code in a clear, educational manner.`
      },
      {
        role: 'user',
        content: `Explain this ${language} code:\n\n${code}`
      }
    ];

    try {
      const response = await this.makeRequest('/chat/completions', {
        model: 'anthropic/claude-3-haiku',
        messages: messages,
        temperature: 0.3,
        max_tokens: 1000,
        stream: false
      });

      return response.choices?.[0]?.message?.content || 'Unable to explain code.';
    } catch (error) {
      console.error('Code explanation failed:', error);
      throw new Error('Failed to explain code. Please try again.');
    }
  }
}


export default APIService;


